import { Link } from "@tanstack/react-router";
import { Search } from "lucide-react";
import { useDeferredValue, useState, type ReactElement } from "react";
import { Input } from "@ndd/ui";
import { normalizeRdName } from "@/components/v2/rd-list";
import { avatarClass, initials } from "@/lib/avatar";
import { isRosterRowNotPublished, type RosterMetricsRow } from "@/lib/metrics";
import {
  NOT_PUBLISHED_LANE_NOTE,
  ROSTER_METRICS,
  formatRollup,
  groupRosterIntoLanes,
  rollupSummary,
  rosterCountLabel,
  type RosterLaneGroup,
} from "@/lib/roster-lanes";
import {
  COLUMN_LABEL,
  MetricCell,
  NotPublishedCell,
} from "@/components/v2/roster-row-cells";
import {
  THEMED_BAND,
  THEMED_CARD,
  THEMED_DISPLAY_VALUE,
  THEMED_DIVIDE,
  THEMED_EYEBROW,
  THEMED_ROW_HOVER,
  THEMED_RULE,
} from "@/lib/always-look";
import { cn } from "@/lib/utils";

/*
 * /rds with supervisor-metrics on (NO-559): rollup strip, column header, and
 * one card per non-empty lane. Lane and cell logic lives in lib/roster-lanes;
 * this file only lays it out. Figma: RD Roster — default 102:3008.
 */

// Mirrors RDList's search threshold; rd-list.tsx is not touched by this ticket.
const ROSTER_SEARCH_THRESHOLD = 20;

const LANE_GRID = "md:grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1.4fr))]";

// Under the always theme the rollup strip shares the rows' column grid, so
// each figure sits over its column (NO-792).
const ROLLUP_GRID =
  "md:grid-cols-4 brand:md:grid-cols-[minmax(0,2fr)_repeat(3,minmax(0,1.4fr))]";

export function RosterLanes({ rows }: { rows: RosterMetricsRow[] }): ReactElement {
  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const showSearch = rows.length > ROSTER_SEARCH_THRESHOLD;
  const normalizedQuery = normalizeRdName(deferredQuery);
  const filtered = normalizedQuery
    ? rows.filter((rd) =>
        normalizeRdName([rd.name, rd.supervisorName ?? ""].join(" ")).includes(
          normalizedQuery
        )
      )
    : rows;
  const lanes = groupRosterIntoLanes(filtered);

  return (
    <div className="space-y-4">
      {showSearch && <RosterSearch query={query} onChange={setQuery} />}
      <RollupStrip rows={filtered} />
      <div className={cn("hidden md:grid gap-4 px-5", LANE_GRID)}>
        <div className={COLUMN_LABEL}>RD</div>
        {ROSTER_METRICS.map((column) => (
          <div key={column.name} className={COLUMN_LABEL}>
            {column.label}
          </div>
        ))}
      </div>
      {lanes.length > 0 ? (
        lanes.map((lane) => <LaneCard key={lane.lane} lane={lane} />)
      ) : (
        <div
          className={cn(
            "rounded-xl border border-border bg-card shadow-sm px-5 py-10 text-center text-sm text-muted-foreground",
            THEMED_CARD
          )}
        >
          No RDs match your search.
        </div>
      )}
    </div>
  );
}

function RosterSearch({
  query,
  onChange,
}: {
  query: string;
  onChange: (next: string) => void;
}): ReactElement {
  return (
    <div className="relative w-full sm:max-w-md">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-subtle" />
      <Input
        type="text"
        value={query}
        onChange={(event) => onChange(event.target.value)}
        placeholder="Search RDs…"
        aria-label="Search RDs"
        className="h-10 rounded-[10px] border-border bg-card pl-10 pr-3 text-sm shadow-sm placeholder:text-subtle"
      />
    </div>
  );
}

function RollupStrip({ rows }: { rows: RosterMetricsRow[] }): ReactElement {
  const summary = rollupSummary(rows);
  const figures = formatRollup(summary);
  const cells: Array<{ label: string; value: string }> = [
    { label: "Roster", value: rosterCountLabel(summary) },
    ...ROSTER_METRICS.map((column, index) => ({
      label: column.label,
      value: figures[index],
    })),
  ];

  return (
    <dl
      className={cn(
        "grid grid-cols-2 rounded-xl border border-border bg-card shadow-sm divide-y md:divide-y-0 md:divide-x divide-border",
        ROLLUP_GRID,
        THEMED_CARD,
        THEMED_DIVIDE
      )}
    >
      {cells.map((cell, index) => (
        <div key={cell.label} className="px-5 py-4">
          <dt
            className={cn(
              "uppercase tracking-wider text-muted-foreground",
              index === 0 ? "text-meta font-extrabold" : "text-caption font-medium",
              THEMED_EYEBROW
            )}
          >
            {cell.label}
          </dt>
          <dd
            className={cn(
              "mt-1 text-xl font-bold text-foreground",
              THEMED_DISPLAY_VALUE
            )}
          >
            {cell.value}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function LaneCard({ lane }: { lane: RosterLaneGroup }): ReactElement {
  return (
    <section
      aria-label={lane.label}
      className={cn(
        "rounded-xl border border-border bg-card shadow-sm overflow-hidden",
        THEMED_CARD
      )}
    >
      <h2
        className={cn(
          "flex flex-wrap items-center gap-2 py-3 px-5 bg-muted border-b border-border text-xs tracking-wider text-muted-foreground font-semibold uppercase",
          THEMED_BAND,
          THEMED_EYEBROW,
          "brand:py-1.5"
        )}
      >
        {lane.label}
        <span className="font-medium text-subtle brand:font-normal brand:text-muted-foreground">
          {lane.rows.length}
        </span>
        {lane.lane === "not-published" && (
          <span className="font-normal normal-case tracking-normal text-body-sm text-muted-foreground">
            · {NOT_PUBLISHED_LANE_NOTE}
          </span>
        )}
      </h2>
      {lane.rows.map((rd) => (
        <RosterRow key={rd.id} rd={rd} />
      ))}
    </section>
  );
}

const ROW_CLASS = cn(
  "grid gap-3 py-4 px-5 border-b border-border last:border-b-0 bg-card hover:bg-muted transition-colors md:gap-4 md:items-center",
  THEMED_RULE,
  THEMED_ROW_HOVER
);

function RdNameLink({ rd }: { rd: RosterMetricsRow }): ReactElement {
  return (
    <Link
      to="/rds/$rdId"
      params={{ rdId: rd.slug }}
      className="flex items-center gap-3 min-w-0"
    >
      <span
        aria-hidden="true"
        className={cn(
          "flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white",
          avatarClass(rd.id),
          // Initials in display type, as the RD page's avatar draws them.
          "brand:font-sans brand:font-bold"
        )}
      >
        {initials(rd.name)}
      </span>
      <span className="text-base font-semibold text-foreground truncate">
        {rd.name}
      </span>
    </Link>
  );
}

function RosterRow({ rd }: { rd: RosterMetricsRow }): ReactElement {
  // An uncovered RD keeps the row shape of every other row — em dash, sub-label
  // and bare track per column — and is told apart by its "No Data" sub-label
  // rather than by a different silhouette (NO-618, QA 2026-09-17).
  if (isRosterRowNotPublished(rd)) {
    return (
      <div className={cn(ROW_CLASS, LANE_GRID)}>
        <RdNameLink rd={rd} />
        {ROSTER_METRICS.map((column) => (
          <NotPublishedCell key={column.name} label={column.label} />
        ))}
      </div>
    );
  }

  return (
    <div className={cn(ROW_CLASS, LANE_GRID)}>
      <RdNameLink rd={rd} />
      {ROSTER_METRICS.map((column) => (
        <MetricCell
          key={column.name}
          name={column.name}
          label={column.label}
          envelope={rd.metrics.metrics[column.name]}
        />
      ))}
    </div>
  );
}
